import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CellClickEvent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { GroupApprovalData } from '../../../raci/models/group-approval-data';
import { RaciService } from '../../../raci/services/raci.service';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Handles approval matrix component
 */
@Component({
  selector: 'approval-matrix',
  templateUrl: './approval-matrix.component.html',
  styleUrls: ['./approval-matrix.component.css']
})
export class ApprovalMatrixComponent implements OnInit, OnDestroy {

  /**
   * The approval matrix data.
   */
  @Input() set approvalMatrix(value: any) {
    if (value) {
      this.resetSelections();
      this.data = value;
      this.data.forEach(approval => {
        this.mapApprovalDates(approval);
        approval.isSelected = false;
      });

      this.projectName = this.data[0]?.projectName;
      this.manageProjectApprovals();
    }
  }

  /**
   * Show approval matrix
   */
  @Input() set hideApprovalMatrix(value: any) {
    this.showApprovalMatrix = value;
  }

  /**
 * The property to group by.
 */
  @Input() groupBy: string;

  /**
   * The current user.
   */
  @Input() currentUser;

  /**
   * Array of properties grouped by sector.
   */
  propertiesBySector: Array<GroupApprovalData> = [];

  /**
   * Array of properties grouped by region.
   */
  propertiesByRegion: Array<GroupApprovalData> = [];

  /**
   * Data for the group.
   */
  groupData: any;

  /**
   * The name of the project.
   */
  projectName = "";

  /**
   * Indicates if project approval is selected.
   */
  isProjectApprovalSelected: boolean;

  /**
   * The selected approval.
   */
  selectedApproval: any;

  /**
   * The index of the selected approval.
   */
  selectedApprovalIndex: number;

  /**
   * The selected group.
   */
  selectedGroup: string;

  /**
   * Array of project approvals.
   */
  projectApproval: Array<any> = [];

  /**
   * Array of selected approvals.
   */
  selectedApprovals: Array<any> = [];

  /**
   * Indicates if a project is selected.
   */
  isProjectSelected = false;

  /**
   * The approval comment.
   */
  approvalComment: any;

  /**
   * Data array.
   */
  data: Array<any> = [];

  /**
   * Event emitter for updating user dashboard complete task status.
   */
  @Output() updateUserDashboardCompleteTaskStatus = new EventEmitter<boolean>();

  /**
   * Array of subscriptions.
   */
  private _subscriptions = new Array<Subscription>();

  /**
   * Indicates whether to show the approval matrix.
   */
  showApprovalMatrix = false;

  /**
   * The selected report ID.
   */
  selectedReportId: any;

  /**
   * The selected portfolio ID.
   */
  selectedPortfolioId: any;

  /**
   * The selected project ID.
   */
  selectedProjectId: any;

  /**
   * Valuation reports.
   */
  valuationReports: any;

  /**
   * The mode for selecting items.
   */
  mode: SelectableMode = "single";

  /**
   * Indicates whether dragging is enabled.
   */
  drag = false;

  /**
   * Selectable settings.
   */
  selectableSettings: SelectableSettings;

  /**
   * Indicates whether the approval dialog is displayed.
   */
  isApprovalDialogDisplayed = false;

  /**
   * Indicates whether the approve action is triggered.
   */
  isApproveTriggered = false;

  /**
   * Selected regional group from approval dialog
   */
  selectedRegionalGroup: any;

  /**
   * Selected business unit from approval dialog
   */
  selectedBusinessUnit: any;

  /**
   * Constructor
   * @param _raciService
   * @param _raciNavigationService
   * @param eventBus
   */
  constructor(private _raciService: RaciService,
    private _raciNavigationService: RaciNavigationService,
    private eventBus: EventBusService) {
    this.setSelectableSettings();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
  * OnInit
  */
  ngOnInit(): void {
    this.projectName = this.data[0]?.projectName;
    this.getReports();
    this._subscriptions.push(this._raciNavigationService._openReport.subscribe(data => {
      if (data) {
        this.createReportLink();
      }
    }));

    this.showRaciApprovalDialog();

    this.eventBus.subscribeEvent("closeRaciApprovalMatrixDialog", (_payload, _options) => {
      this.isApprovalDialogDisplayed = false;
    });
  }

  /**
   * On changes actions
   */
  ngOnChanges() {
    if (this.data) {
      this.selectedPortfolioId = this.data[0]?.portfolioId;
      this.selectedProjectId = this.data[0]?.projectId;
      this.groupProperties();
      this.manageProjectApprovals();
    }
  }

  /**
 * Create report link
 */
  createReportLink() {
    if (this.selectedReportId) {
      const absUrl = window.location.href;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/pd
      let url = absUrl.split("#")[0];//http://localhost/polluxApp/polluxValuation/app/pollux.html

      url += "#/rd";
      url += "?ViewReportId=" + this.selectedReportId;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/rd?ViewReportId=1216
      window.open(url, '_blank');
    }
  }

  /**
 * Get Reports of valuation event
 */
  getReports() {
    this._subscriptions.push(this._raciService.getReports(this.selectedProjectId, this.selectedPortfolioId).subscribe(data => {
      if (data) {
        this.valuationReports = data;
      }
    }));
  }

  /**
 * Set Selectable Settings
 */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Create approval based on project
   */
  manageProjectApprovals() {
    this.projectApproval = [{}];
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      this.data.sort((approvalA, approvalB) => this.getMostCommonApprover(this.data, approvalIndex, approvalA, approvalB));
      const mostCommonApprover = this.data[0];
      const pendingApproval = this.data.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      this.projectApproval[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      this.projectApproval[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      this.projectApproval[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      this.projectApproval[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
   * Groups properties by region
   */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.data.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.region })) {
        this.propertiesByRegion.push(new GroupApprovalData({ "group": property.region }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.region }).tasks.push(property);
    });

    this.propertiesByRegion.forEach(region => {
      this.setMostCommonApprover(region);
    });

    this.checkForUnknownGroup(this.propertiesByRegion);
  }

  /**
   * Checks for the mos commpon approver
   * @param approvalList Approvals list
   * @param approvalIndex Approval index (eg. 1,2,3)
   * @param approvalA First approval for comparison
   * @param approvalB Second approval for comparison
   */
  private getMostCommonApprover(approvalList: any[], approvalIndex: number, approvalA: any, approvalB: any) {
    return approvalList.filter(v => v[`approval${approvalIndex}`] === approvalB[`approval${approvalIndex}`]).length - approvalList.filter(v => v[`approval${approvalIndex}`] === approvalA[`approval${approvalIndex}`]).length;
  }

  /**
   * Sets most common approvers for specified group
   * @param group Group object
   */
  private setMostCommonApprover(group: any) {
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      const mostCommonApprover = group.tasks.sort((approvalA, approvalB) => this.getMostCommonApprover(group.tasks, approvalIndex, approvalA, approvalB))[0];
      const pendingApproval = group.tasks.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      group.approvals[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      group.approvals[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      group.approvals[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      group.approvals[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
   * Check if the group list contains a group without name and sets it name to "Unknown"
   * @param groupList Group list
   */
  private checkForUnknownGroup(groupList: any[]) {
    const unknownSector = groupList.find(item => { return !item.group });
    if (unknownSector) {
      unknownSector.group = "Unknown";
    }
  }

  /**
   * Groups properties by sector
   */
  private groupBySector() {
    this.propertiesBySector = [];
    this.data.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.sector })) {
        this.propertiesBySector.push(new GroupApprovalData({ "group": property.sector }));
      }
      this.propertiesBySector.find(item => { return item.group == property.sector }).tasks.push(property);
    });

    this.propertiesBySector.forEach(sector => {
      this.setMostCommonApprover(sector);
    });

    this.checkForUnknownGroup(this.propertiesBySector);
  }

  /**
   * On approval selection event
   * @param cell Selected kendo grid cell
   */
  public onApprovalSelection(cell: CellClickEvent, isProjectApproval: boolean) {
    this.selectedApproval = null;
    const dataItem = cell.dataItem;
    this.selectedApprovalIndex = this.getApprovalUserIndex(dataItem);
    this.selectedApproval = dataItem;
    this.isProjectApprovalSelected = isProjectApproval;
    this.approvalComment = '';
    this.selectedGroup = null;

    // Clicking on the other columns, enable opening report
    const report = this.valuationReports?.find(report => { return report.PropertyId == cell.dataItem.propertyId })

    if (report) {
      this._raciNavigationService.onSelectRow(true);
      this.selectedReportId = report.Oid;
    }
  }

  /**
  * Opens modal for approval or rejection
  */
  approveRejectModal() {
    if (this.isApproveTriggered) {
      this.approve();
    } else {
      this.reject();
    }
  }

  /**
 * On group selection event
 * @param cell Cell click event
 * @param item Selected item
 */
  onGroupSelection(cell: CellClickEvent, item: any) {
    // Click should be enabled only for columns containing approvals
    if (cell.columnIndex > 1) {
      this.selectedApproval = null;
      const dataItem = cell.dataItem;
      this.selectedApprovalIndex = cell.columnIndex - 1;
      this.selectedApproval = dataItem;
      this.approvalComment = '';
      this.selectedGroup = item.group;

      // Prevent selection of approved approvals and other user approvals
      if (this.selectedApproval[`dateApproved${this.selectedApprovalIndex}`]) {
        this.selectedApproval = null;
        this.isProjectApprovalSelected = false;
        this.selectedApprovalIndex = null;
      }

      if (this.selectedApproval) {
        this.approveRejectModal();
      }
    }
  }

  /**
 * Selection change event for grouped data
 * @param selectedGroup Selected group
 */
  onSelectionChange(selectedGroup: string) {
    this.groupData.forEach(group => {
      if (group.group != selectedGroup) {
        group.selectedRows = [];
      }
    });
  }

  /**
  * Give approval to selected approval
  */
  approve() {
    if (this.isProjectSelected) {
      this.approveOnProjectLevel();
    }
    else if (this.groupBy && this.selectedGroup) {
      this.approveOnGroupLevel();
    }
    else {
      this.singleApprove();
    }
  }

  /**
   * Approve single approval
   */
  private singleApprove() {
    const approvals = [];
    this.selectedApprovals.forEach(approval => {
      approval[`dateApproved${this.getApprovalIndex(approval)}`] = new Date();
      approvals.push(approval);
    });
    this.subscribeToApproveEvent(approvals);
  }

  /**
   * Give project level approval
   */
  private approveOnProjectLevel() {
    const approvals = [];
    this.selectedApprovals.forEach(property => {
      if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
        this.setPropertyApprovalData(property);
        approvals.push(property);
      }
      this.projectApproval[0][`dateApproved${this.getApprovalIndex(property)}`] = new Date();
    });
    this.subscribeToApproveEvent(approvals);
  }

  /**
   * Approve approvals on group level
   */
  private approveOnGroupLevel() {
    const approvals = [];
    const selectedGroups = this.groupData.filter(groupItem => groupItem.isSelected);
    selectedGroups.forEach(group => {
      group.tasks.forEach(property => {
        if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
          this.setPropertyApprovalData(property);
        }
        group.approvals[0][`dateApproved${this.getApprovalIndex(property)}`] = new Date();
        approvals.push(property);
      });
    })

    this.subscribeToApproveEvent(approvals);
  }

  /**
   * Sets property approval data
   * @param property Property data
   */
  private setPropertyApprovalData(property: any) {
    property[`approval${this.getApprovalIndex(property)}`] = this.currentUser.OID;
    property[`approvalUser${this.getApprovalIndex(property)}`] = this.currentUser.User_Description;
    property[`dateApproved${this.getApprovalIndex(property)}`] = new Date();
  }

  /**
 * Reject approval task
 */
  public reject() {
    if (this.isProjectSelected) {
      this.rejectOnProjectLevel();
    }
    else if (this.groupBy && this.selectedGroup) {
      this.rejectOnGroupLevel();
    }
    else {
      this.singleRejection();
    }
  }

  /**
   * Reject single approval
   */
  private singleRejection() {
    const approvals = [];
    this.selectedApprovals.forEach(approval => {
      approval[`dateRejected${this.getApprovalIndex(approval)}`] = new Date();
      approvals.push(approval);
    });
    this.subscribeToRejectEvent(approvals);
  }

  /**
  * Sets property rejection data
  * @param property Property info
  */
  private setPropertyRejectionData(property: any) {
    property[`dateRejected${this.getApprovalIndex(property)}`] = new Date();
    property[`approval${this.getApprovalIndex(property)}`] = this.currentUser.OID;
    property[`approvalUser${this.getApprovalIndex(property)}`] = this.currentUser.User_Description;
  }

  /**
  * Reject on group level
  */
  private rejectOnGroupLevel() {
    const approvals = [];
    const selectedGroups = this.groupData.filter(groupItem => groupItem.isSelected);
    selectedGroups.forEach(group => {
      group.tasks.forEach(property => {
        this.markPropertyAsRejected(group.approvals[0], property, approvals);
      });
    });

    this.subscribeToRejectEvent(approvals);
  }

  /**
  * Reject approvals on project level
  */
  private rejectOnProjectLevel() {
    const approvals = [];
    this.selectedApprovals.forEach(property => {
      this.markPropertyAsRejected(this.projectApproval[0], property, approvals);
    });

    this.subscribeToRejectEvent(approvals);
  }

  /**
   * Marks property as rejected
   * @param approval Approval to which the property is linked to
   * @param property Property info
   * @param approvals List of rejected approvals
   */
  markPropertyAsRejected(approval: any, property: any, approvals: any) {
    if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
      this.setPropertyRejectionData(property);
      approvals.push(property);
    }
    approval[`dateRejected${this.getApprovalIndex(property)}`] = new Date();
  }

  /**
   * Subscribes to reject event
   * @param approvals Approval data
   */
  private subscribeToRejectEvent(approvals: Array<any>) {
    this._subscriptions.push(this._raciService.rejectApprovals(approvals, this.approvalComment, this.currentUser.OID).subscribe(data => {
      if (data) {
        this.resetSelections();
        approvals.forEach(approval => approval.isSelected = false);
        this.refreshProjectAndGroupApprovals();
        this.isApprovalDialogDisplayed = false;
        this.groupData.forEach(group => group.isSelected = false);
        this.eventBus.broadcastEvent("closeRaciApprovalMatrixDialog");
      }
    }));
  }

  /**
  * Group proerties by sector or region.
  */
  private groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
    this._raciNavigationService.onSelectRow(false);
    this.groupData.forEach(group => {
      const selectedTasks = group.tasks.filter(task => task.isSelected);
      if (selectedTasks.length > 0 && selectedTasks.length == group.tasks.filter(task => !this.isPropertyApproved(task)).length) {
        group.isSelected = true;
      }
    })
  }

  /**
   * Refresh group by/project level approvals
   */
  private refreshProjectAndGroupApprovals() {
    if (!this.groupBy) {
      this.manageProjectApprovals();
    }
    else {
      this.groupProperties();
    }
  }

  /**
 * Resets grid selections
 */
  private resetSelections() {
    this.selectedApproval = null;
    this.selectedApprovalIndex = null;
    this.isProjectApprovalSelected = false;
    this.approvalComment = '';
    this.selectedApprovals = [];
    this.isProjectSelected = false;
  }

  /**
   * Subscribes to approve event action on raci service
   * @param approvals Approval data
   */
  private subscribeToApproveEvent(approvals: Array<any>) {
    this._subscriptions.push(this._raciService.approve(approvals, this.approvalComment, this.currentUser.OID).subscribe(data => {
      if (data) {
        this.resetSelections();
        this.refreshProjectAndGroupApprovals();
        this.updateUserDashboardCompleteTaskStatus.next(true);
        approvals.forEach(approval => approval.isSelected = false);
        this.groupData.forEach(group => group.isSelected = false);
        this.isApprovalDialogDisplayed = false;
        this._raciNavigationService.onShowApproveIcon(false);
        this.eventBus.broadcastEvent("closeRaciApprovalMatrixDialog");
      }
    }));
  }

  /**
  * Maps approval dates
  * @param approval Approval
  */
  private mapApprovalDates(approval: any) {
    approval.dateApproved1 = approval.dateApproved1 ? new Date(approval.dateApproved1) : null;
    approval.dateApproved2 = approval.dateApproved2 ? new Date(approval.dateApproved2) : null;
    approval.dateApproved3 = approval.dateApproved3 ? new Date(approval.dateApproved3) : null;
    approval.dateRejected1 = approval.dateRejected1 ? new Date(approval.dateRejected1) : null;
    approval.dateRejected2 = approval.dateRejected2 ? new Date(approval.dateRejected2) : null;
    approval.dateRejected3 = approval.dateRejected3 ? new Date(approval.dateRejected3) : null;
  }

  /**
   * On propery select
   * @param selectedItem Selected item
   * @param groupState Is data grouped
   */
  onPropertySelect(selectedItem: any, groupState = false) {
    selectedItem.isSelected = !selectedItem.isSelected;
    if (selectedItem.isSelected) {
      this.markPropertyAsSelected(selectedItem, groupState);
    } else {
      this.markPropertyAsUnselected(selectedItem, groupState);
    }

    this.shouldShowHideApprovalIcon();
  }

  /**
  * Marks property as unselected
  * @param selectedItem Property that is being unselected
  * @param groupState Group by state (refion/sector/none)
  */
  markPropertyAsUnselected(selectedItem: any, groupState: any) {
    this.removeFromSelectedApprovals(selectedItem);
    if (groupState) {
      this.groupData.find(group => {
        return group.tasks.find(task => { return task.propertyId == selectedItem.propertyId; });
      }).isSelected = false;
    }
  }

  /**
   * Marks property as selected
   * @param selectedItem Property that is being selected
   * @param groupState Group by state (refion/sector/none)
   */
  markPropertyAsSelected(selectedItem: any, groupState: any) {
    this.selectedApprovals.push(selectedItem);
    if (this.selectedApprovals.length == this.data.filter(item => !this.isPropertyApproved(item)).length) {
      this.isProjectSelected = true;
    }
    if (groupState) {
      const group = this.groupData.find(data => {
        return data.tasks.find(task => { return task.propertyId == selectedItem.propertyId; });
      });
      this.shouldGroupBeSeleted(group);
    }
  }

  /**
   * Check should group needs to be selected
   * @param group Group
   */
  shouldGroupBeSeleted(group: any) {
    if (!group.tasks.find(task => !task.isSelected)) {
      group.isSelected = true;
    }
  }

  /**
   * Remove from selected appovals list
   * @param item
   */
  removeFromSelectedApprovals(item) {
    const index = this.selectedApprovals.findIndex(approval => approval.propertyId == item.propertyId);
    this.selectedApprovals.splice(index, 1);
    this.isProjectSelected = false;
  }

  /**
   * Check is item selecteed
   * @param item Item
   */
  isSelected(item: any): boolean {
    return this.selectedApprovals.find(approval => approval.propertyId == item.propertyId);
  }

  /**
   * Check is property Approved
   * @param item
   */
  isPropertyApproved(item: any): boolean {
    for (let index = 1; index < 4; index++) {
      if (item[`approval${index}`] == this.currentUser.OID) {
        return item[`dateApproved${index}`] != null;
      }
    }
    return false;
  }

  /**
   * Check is property rejected
   * @param item
   */
  isPropertyRejected(item: any): boolean {
    for (let index = 1; index < 4; index++) {
      if (item[`approval${index}`] == this.currentUser.OID) {
        return item[`dateRejected${index}`] != null && !item[`dateApproved${index}`];
      }
    }

    return false;
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = !this.isProjectSelected;
    if (this.isProjectSelected) {
      this.selectedApprovals = [];
      this.data.forEach(property => {
        this.addToSelectedApprovals(property);
      });
      this.groupData.forEach(group => {
        this.updateGroupSelectionStatus(group);
      });
    } else {
      this.selectedApprovals = [];
      this.groupData.forEach(group => group.isSelected = false);
      this.data.forEach(property => {
        property.isSelected = false;
      });
    }
    this.shouldShowHideApprovalIcon();
  }

  /**
   * Update group seletion status
   * @param group Group
   */
  updateGroupSelectionStatus(group: any) {
    const unApprovedTask = group.tasks.find(task => {
      return !this.isPropertyApproved(task);
    });
    if (unApprovedTask != null) {
      group.isSelected = true;
    } else {
      group.isSelected = false;
    }
  }

  /**
   * Select all properties of group
   * @param groupItem Group item
   */
  selectAllPropertiesOfGroup(groupItem: any) {
    this.selectedGroup = groupItem.group;
    groupItem.isSelected = !groupItem.isSelected;
    if (groupItem.isSelected) {
      groupItem.tasks.forEach(task => {
        this.addToSelectedApprovals(task);
      });
      if (this.selectedApprovals.length == this.getUnapprovedProperties().length) {
        this.isProjectSelected = true;
      }
    } else {
      groupItem.tasks.forEach(task => {
        if (!this.isPropertyApproved(task)) {
          task.isSelected = false;
          this.removeFromSelectedApprovals(task);
        }
      });
    }

    this.shouldShowHideApprovalIcon();
  }

  /**
   * Get only unapproved properties
   */
  getUnapprovedProperties() {
    return this.data.filter(property => !this.isPropertyApproved(property) && (this.currentUser.OID == property.approval1 || this.currentUser.OID == property.approval2 || this.currentUser.OID == property.approval3));
  }

  /**
   * Add to selected approvals list
   * @param item
   */
  addToSelectedApprovals(item) {
    if (!this.isPropertyApproved(item)) {
      item.isSelected = true;
      this.selectedApprovals.push(item)
    }
  }

  /**
   * Check is grouped properties approved
   * @param group Groupe
   */
  isGroupPropertyApproved(group: any): boolean {
    const unApprovedTask = group.tasks.find(task => {
      return !this.isPropertyApproved(task);
    });

    return unApprovedTask == null;
  }

  /**
   * Check is grouped properties rejected
   * @param group Groupe
   */
  isGroupPropertyRejected(group: any): boolean {
    const unRejectedTask = group.tasks.find(task => {
      return !this.isPropertyRejected(task);
    });

    return unRejectedTask == null;
  }

  /**
 * Check is project selectable
 */
  isProjectSelectable(): boolean {
    const unApprovedTask = this.data.find(property => {
      return !this.isPropertyApproved(property);
    });

    return !!unApprovedTask;
  }

  /**
 * Check is project rejected
 */
  isProjectRejected(): boolean {
    const approvedTasks = this.data.filter(property => {
      return this.isPropertyRejected(property);
    });

    return !this.isProjectSelected && !!approvedTasks;
  }

  /**
 * Check iif the project rejected checkbox is checked
 */
  isProjectRejectedCheckbox(): boolean {
    const approvedTasks = this.data.filter(property => {
      return this.isPropertyRejected(property);
    });

    return !this.isProjectSelected && approvedTasks?.length == this.data.length;
  }

  /**
   * Get approval index
   * @param item Item
   */
  getApprovalIndex(item: any) {
    const keyValues = Object.entries(item);
    for (let index = 1; index < 4; index++) {
      const approval = keyValues.find(entry => entry[0].includes("approval" + index));
      if (approval[1] == this.currentUser.OID) {
        return index;
      }
    }
  }

  /**
   * Check if approved icon appear
   */
  shouldShowHideApprovalIcon() {
    this._raciNavigationService.onShowApproveIcon(this.selectedApprovals.length > 0);
  }

  /**
  * Returns index of approver
  * @param dataItem DataItem
  */
  private getApprovalUserIndex(dataItem: any) {
    if (dataItem.approval1 === this.currentUser.OID) return 1;
    if (dataItem.approval2 === this.currentUser.OID) return 2;
    if (dataItem.approval3 === this.currentUser.OID) return 3;
  }

  /**
   * Handles show/hide approval dialog
   */
  showRaciApprovalDialog() {
    this._subscriptions.push(this._raciNavigationService._isApprovalDialogDisplayed.subscribe(data => {
      this.isApprovalDialogDisplayed = data;
    }));
  }

  /**
   * Handles on approve action
   */
  onApprove(event: any) {
    if (event) {
      this.approvalComment = event.comment;
      this.selectedRegionalGroup = event.regionalGroup;
      this.selectedBusinessUnit = event.businessUnit;
      this.isApproveTriggered = event.type === 'approve';
      this.approveRejectModal();
    }
  }
}
